import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_USER_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import { useDebounce } from '@/hooks/useDebounce';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { ActionConfig } from '@/interfaces/common.interface';
import {
  Customer,
  ICustomerResponse,
  ICustomerStates,
} from '@/interfaces/customer.interface';
import {
  GetFilterUrl,
  getUserType,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { PlusIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Staffs: React.FC = () => {
  const navigate = useNavigate();
  const { canEdit, canView, canCreate } = usePermissions();
  const [searchValue, setSearchValue] = useState<string>('');
  const debounceValue = useDebounce(searchValue);
  const [state, setState] = useState<ICustomerStates>({
    customer: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
    type: '',
    roleId: '',
    isLoading: false,
    selectedUserId: null,
    showStatusModel: false,
    showVerificationModel: false,
    statusToUpdate: null,
  });

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(state.customer);

  useEffect(() => {
    fetchCustomers();
  }, [
    state.pageInfo.limit,
    state.pageInfo.page,
    debounceValue,
    state.type,
    state.roleId,
  ]);

  const fetchCustomers = async () => {
    try {
      const filterQuery = {
        page: state.pageInfo.page,
        limit: state.pageInfo.limit,
        fullName: debounceValue,
        type: 'staff',
        roleId: state.roleId,
      };
      const query = GetFilterUrl(filterQuery);
      setState((prev) => ({
        ...prev,
        isLoading: true,
      }));
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/list?${query ? `${query}` : ''}`;
      const res = await callApi<ICustomerResponse>(PATH, { method: 'GET' });
      setState((prev) => ({
        ...prev,
        customer: res?.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      }));
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const handleBlockUser = async (userId: string) => {
    try {
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/block/${userId}?isBlocked=false`;
      await callApi(PATH, { method: 'PATCH' });
      handleChangePage(1);
      fetchCustomers();
    } catch (error) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('error in block', error);
    }
  };

  const handleChangePage = (page: number) => {
    setState((prev) => ({
      ...prev,
      pageInfo: {
        ...prev.pageInfo,
        page: page,
      },
    }));
  };

  const handleChangePageSize = (limit: number) => {
    setState((prev) => ({
      ...prev,
      pageInfo: {
        ...prev.pageInfo,
        page: 1,
        limit: limit,
      },
    }));
  };

  const customerColumns: TableColumn<Customer>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (customer) => (
        <span
          className={customer.isEmployee ? 'text-blue-500' : ''}
        >{`${customer.fullName}`}</span>
      ),
      className: 'p-3 font-medium text-gray-900 ',
    },
    {
      key: 'role',
      header: 'Role',
      render: (customer) => (
        <span className={customer.isEmployee ? 'text-blue-500' : ''}>
          {getUserType(customer?.role?.name, customer?.isEmployee)}
        </span>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (customer) => (
        <span className={customer.isEmployee ? 'text-blue-500' : ''}>
          {customer.email}
        </span>
      ),
    },
    {
      key: 'orders',
      header: 'Orders',
      render: (customer) => (
        <span className={customer.isEmployee ? 'text-blue-500' : ''}>
          {customer.orderDetails.orders}
        </span>
      ),
    },
    {
      key: 'isBlocked',
      header: 'Status',
      render: (customer) => {
        return (
          <>
            <StatusDropdown
              options={[
                {
                  label: 'Active',
                  value: 'active',
                  textClass: 'text-green-500',
                  bgClass: 'bg-green-500',
                },
                {
                  label: 'Blocked',
                  value: 'blocked',
                  textClass: 'text-red-500',
                  bgClass: 'bg-red-500',
                },
              ]}
              canEditVal="role"
              value={customer.isBlocked ? 'blocked' : 'active'}
              onChange={() => handleBlockUser(customer.id)}
            />
            {/* <span className={customer.isEmployee ? 'text-blue-500' : ''}>
                              {customer?.isBlocked ? 'Blocked' : 'Active'}
                            </span> */}
          </>
        );
      },
    },
    {
      key: 'loyaltyPoints',
      header: 'Loyalty Point',
      render: (customer) => (
        <span className={customer.isEmployee ? 'text-blue-500' : ''}>
          {customer.loyalty.points}
        </span>
      ),
    },
    {
      key: 'amountSpent',
      header: 'Amount Spend (BHD)',
      render: (customer) => (
        <span className={customer.isEmployee ? 'text-blue-500' : ''}>
          {toFixedThree(customer.orderDetails.spends)}
        </span>
      ),
    },
  ];

  const staffActionConfig: Record<string, ActionConfig<Customer>> = {
    view: {
      text: 'View',
      onClick: (staff: Customer) => {
        navigate(`${RoutesLink.customerDetails}/${staff.id}`);
      },
      permission: () => canView('user'),
    },
    eidt: {
      text: 'Edit',
      onClick: (staff) => {
        navigate(`${RoutesLink.addStaff}/${staff.id}`);
      },
      permission: () => canEdit('user'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(staffActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canView, canEdit],
  );

  return (
    <Layout>
      <PageHeading
        text="Staffs"
        button={
          canCreate('user')
            ? {
                text: 'Add Staff',
                icon: <PlusIcon />,
                onClick: () => navigate(RoutesLink.addStaff),
              }
            : undefined
        }
      />
      <Card>
        <CommonFilter
          inputPlaceHolder="Search for Customers..."
          onSearch={(text) => setSearchValue(text)}
        />
        <CommonTable
          columns={customerColumns}
          data={state.customer}
          showCheckbox={false}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No Customers found"
          loading={state.isLoading}
          actions={actions}
        />
        {state.pageInfo.totalPages > 1 && (
          <Pagination
            isLoading={state.isLoading}
            paginationData={state.pageInfo}
            onPageChange={(page) => handleChangePage(page)}
            onPageSizeChange={handleChangePageSize}
          />
        )}
      </Card>
    </Layout>
  );
};

export default Staffs;
